import React, {useState, useEffect} from 'react';
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { setDisplayTermsPopup, setHasAcceptedTerms } from '../../../state/slices/user';
import apiClient from '../../../auth/apiClient';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@material-ui/core';

import { useStyles } from './styles';
import UserService from '../../../services/UserService';
import useCurrentFacility from '../../../hooks/useCurrentFacility';
import { useAppActionContext } from '../../../providers/AppStore/AppActionProvider';

export const TermsDialog = ({
  termsTitle = 'Terms & Conditions',
  termsContent = 'Scrolling legalese stuff…',
  checkboxLabel = 'I agree to the terms and conditions',
  acceptLabel = 'Accept and Continue',
  cancelLabel = 'Cancel',
  disableBackdropClose = true,
  disableEscClose = false,
}) => {
  const userService = new UserService(apiClient);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [accepted, setAccepted] = useState(false);
  const [daysLeft, setDaysLeft] = useState(30);
  const [daysLabel, setDaysLabel] = useState('');
  const { logOut } = useAppActionContext();
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const { facilityID } = useCurrentFacility();
  const scopeAwareFacilityID = useSelector((state) => state.entityScope?.facilityGroupId || facilityID);
  const creationDate = currentUser?.termsDetails?.creationDate;

  /*
  // this code will be usable once we implement AO-28810
  const fetchTermsContent = async () => {
    try {
      const response = await userService.getTermsContent(currentUser?.UserID, scopeAwareFacilityID)
      console.log(response);
    }catch(e) {
      console.log(e);
    }
  }

  
  useEffect(() => {
    fetchTermsContent();
  }, [])
  */

  const calculateDaysLeft = (date) => {
    if (!date) return 0;
    const creation = new Date(date);
    if (isNaN(creation)) return 0;
    const expiry = new Date(creation.getTime() + 30 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = expiry - now;
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => {
    if(creationDate) setDaysLeft(calculateDaysLeft(creationDate))
  }, [creationDate])

  useEffect(() => {
    const daysLabel = daysLeft === 1 ? '1 Day' : `${daysLeft} Days`;
    setDaysLabel(daysLabel);
  }, [daysLeft])

  const handleClose = (event, reason) => {
    if (disableBackdropClose && reason === 'backdropClick') return;
    if (disableEscClose && reason === 'escapeKeyDown') return;
    dispatch(setDisplayTermsPopup(false));
    setAccepted(false);
    if(daysLeft <=0 ) {
      logOut()
    }
  };

  const handleAccept = async () => {
    try {
      const response = await userService.acceptLatestTerms(currentUser?.UserID, scopeAwareFacilityID, currentUser?.termsDetails?.version);
      dispatch(setDisplayTermsPopup(false));
      dispatch(setHasAcceptedTerms(true));
      setAccepted(false);
    } catch(e) {
      console.error(e);
    }
  }
  
  return (
    <Dialog
      open={currentUser?.displayTermsPopup}
      onClose={handleClose}
      aria-labelledby="terms-dialog-title"
      fullWidth
      maxWidth="sm"
      classes={{ paper: classes.paper }}
    >
      <DialogTitle id="terms-dialog-title" className={classes.title}>
        {termsTitle}
      </DialogTitle>

      <DialogContent dividers className={classes.content}>
        <div className={classes.outer}>
          <div
            className={classes.scrollBox}
            role="document"
            aria-label="Terms content"
            tabIndex={0}
          >
            {typeof termsContent === 'string' ? (
              <Typography variant="body2" component="div" style={{ whiteSpace: 'pre-wrap' }}>
                {termsContent}
              </Typography>
            ) : (
              termsContent
            )}
          </div>

          {!currentUser?.hasAcceptedTerms ?
            <>
            <div className={classes.controlsRow}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  inputProps={{ 'aria-label': checkboxLabel }}
                />
              }
              label={checkboxLabel}
            />

            <div className={classes.buttonsRow}>
              <Button
                variant="contained"
                color="primary"
                disabled={!accepted}
                onClick={handleAccept}
              >
                {acceptLabel}
              </Button>
              <Button variant="outlined" onClick={handleClose}>
                {cancelLabel}
              </Button>
            </div>
          </div>

          <Typography variant="body2" className={classes.footer}>
            You have {daysLabel} to use the platform
          </Typography>
          </> : 
          <div className={classes.controlsRow}>
            <Button variant="outlined" onClick={handleClose}>
              {cancelLabel}
            </Button>
          </div>
          }
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TermsDialog